import { useState } from "react";
import { Lock } from "lucide-react";
import {
  CardElement,
  Elements,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import axios from "axios";

const stripePromise = loadStripe(
  "pk_test_51QeLLmQRmaDbhm5jP8PD9i6vdVuJA7yrKawgMNWPrxlV9dMQwVQq4WZ52cERYzG0SbmYN3LL9VskVwPY3pez3TS100v0AKHIfY"
);

function CheckoutFormContent({ selectedPlan, billingMode, priceId }) {
  const stripe = useStripe();
  const elements = useElements();
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsLoading(true);
    setError(null);

    try {
      const res = await axios.post(`${BASE_URL}/api/stripe/checkout/`, {
        price_id: priceId,
        tier: selectedPlan,
        mode: billingMode,
      });

      const result = await stripe.confirmCardPayment(res.data.id, {
        payment_method: {
          billing_details: {
            name: `${e.target.firstName.value} ${e.target.lastName.value}`,
            email: e.target.email.value,
            address: {
              line1: e.target.address.value,
              city: e.target.city.value,
              postal_code: e.target.zipCode.value,
            },
          },
          card: elements.getElement(CardElement),
        },
      });

      if (result.error) {
        console.error(result.error.message);
        setError(result.error.message);
      } else if (result.paymentIntent?.status === "succeeded") {
        console.log(" Payment successful!");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white shadow-sm p-6 border rounded-xl"
    >
      <h3 className="mb-6 font-semibold text-xl">Billing Information</h3>

      <div className="space-y-4 mb-6">
        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              name="firstName"
              id="firstName"
              placeholder="John"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input name="lastName" id="lastName" placeholder="Doe" required />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            name="email"
            id="email"
            type="email"
            placeholder="john.doe@example.com"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address">Address</Label>
          <Input
            name="address"
            id="address"
            placeholder="123 Main St"
            required
          />
        </div>

        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="city">City</Label>
            <Input name="city" id="city" placeholder="New York" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="zipCode">ZIP Code</Label>
            <Input name="zipCode" id="zipCode" placeholder="10001" required />
          </div>
        </div>
      </div>

      <h3 className="mb-6 font-semibold text-xl">Payment Information</h3>

      <div className="space-y-4 mb-8">
        <div className="space-y-2">
          <Label htmlFor="card">Card Information</Label>
          <div className="p-3 border rounded-md">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": { color: "#aab7c4" },
                  },
                  invalid: { color: "#9e2146" },
                },
              }}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 mb-4 p-3 rounded-md text-red-600 text-sm">
          {error}
        </div>
      )}

      <Button
        type="submit"
        className="bg-green-600 hover:bg-green-700 w-full text-white"
        disabled={!stripe || isLoading}
      >
        {isLoading ? (
          <span className="flex items-center">
            <svg
              className="mr-3 -ml-1 w-5 h-5 text-white animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Processing...
          </span>
        ) : (
          <span className="flex justify-center items-center">
            <Lock className="mr-2 w-4 h-4" />
            Complete Payment
          </span>
        )}
      </Button>

      <div className="flex justify-center items-center mt-4 text-gray-500 text-sm text-center">
        <Lock className="mr-1 w-4 h-4 text-green-600" />
        <span>Secure checkout powered by Stripe</span>
      </div>
    </form>
  );
}

export default function CheckoutForm({ selectedPlan, billingMode, priceId }) {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutFormContent
        selectedPlan={selectedPlan}
        billingMode={billingMode}
        priceId={priceId}
      />
    </Elements>
  );
}
